import { useChatMessageQueue } from '../../hooks/useChatMessageQueue';
import ChatQueueStatus from '../MessagesChat/ChatQueueStatus';
import ChatArchiveSelection, {ChatArchiveCheckbox, useChatArchive, hasArchiveMedia} from '../MessagesChat/ChatArchiveSelection';
import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal, ModalDialog, Sheet, Stack, Box, Typography, IconButton,
  ModalOverflow, Button, CircularProgress,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';

import MessageInput from '../MessagesChat/MessageInput';
import ChatBubble from '../MessagesChat/ChatBubble';
import AvatarWithStatus from '../MessagesChat/AvatarWithStatus';
import MessageContextMenu from '../MessagesChat/MessageContextMenu';
import {
  CHAT, MOTION, canvasBackground, thinScrollbar, dayKey, dayLabel,
  dateChip, dayChipWrap, unreadDivider, iconColor, ellipsis,
} from '../MessagesChat/chatTheme';

import {
  API_URL, APIGetChatMessagesById,
  APISendMessage,
  APISendVoiceMessage,
  createWebSocketConnection,
  APIClientChatAddable,
  APIClientChatAddMember,
  APIMarkMessagesAsRead,
  APIDeleteMessage,
  APIEditMessage,
  APIToggleMessageReaction,
} from '../../api';
import { LayoutContext } from '../../context';

// Сообщения одного автора подряд в пределах этого окна склеиваются в серию.
const GROUP_WINDOW_MS = 5 * 60 * 1000;
// Пауза перед отправкой пачки «вот это я увидел» — чтобы быстрая прокрутка
// не выдавала запрос на каждое сообщение.
const READ_FLUSH_MS = 400;

const isTmp = (id) => String(id).startsWith('tmp-');

/**
 * Сообщить карточке заявки, что часть непрочитанных погасла.
 *
 * Раньше чат помечал всё прочитанным при загрузке, и бейдж на кнопке
 * «Открыть чат» гас сам собой при следующей загрузке списка. Теперь отметка
 * идёт по факту показа сообщения, поэтому счётчик надо уменьшать по ходу
 * дела. Событие на window — потому что модалка живёт в App.js и с карточкой
 * напрямую не связана; так же в этом проекте устроен 'client_unauthorized'.
 */
const notifyRead = (chatId, count) => {
  if (!chatId || !count) return;
  try {
    window.dispatchEvent(new CustomEvent('client_chat_read', {
      detail: { chatId, count },
    }));
  } catch (e) { /* CustomEvent может быть недоступен в старых движках */ }
};

/**
 * Чат заявки в кабинете клиента.
 *
 * Тот же чат, что у сотрудников: липкие разделители дат по секциям, ответы,
 * правка, реакции, контекстное меню, голосовые, просмотрщик фотографий,
 * мгновенная отправка и граница непрочитанного.
 *
 * Чего здесь НЕТ намеренно — галочек «прочитано» и списка «кто посмотрел».
 * Клиенту не показываем, кто из сотрудников и когда открыл его сообщение.
 * Обратное направление работает: прочтение клиента фиксируется по факту
 * показа сообщения на экране, и сотрудники видят его у себя.
 */
export default function ClientChatModal({ open, chatId, title, applicationId, onClose }) {
  const { account } = useContext(LayoutContext);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);

  const [textAreaValue, setTextAreaValue] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);
  const [menu, setMenu] = useState(null);
  const [actionError, setActionError] = useState('');
  const [showJump, setShowJump] = useState(false);
  const clientToken = localStorage.getItem('client_token');
  const sessionKey = `${chatId || ''}:${clientToken || ''}`;
  const activeSessionRef = useRef(null);
  activeSessionRef.current = open && chatId && clientToken ? sessionKey : null;
  const loadRequestRef = useRef(null);
  const [attachmentReset, resetAttachmentInput] = useState(0);
  const attachmentQueue = useChatMessageQueue({
    sessionKey, enabled: Boolean(open && chatId && clientToken),
    isCurrent: () => localStorage.getItem('client_token') === clientToken,
    send: fd => APISendMessage(chatId, fd, clientToken),
    onMessage: sent => {
      setMessages(prev => prev.some(m => m.id === sent.id)
        ? prev.map(m => m.id === sent.id ? sent : m) : [...prev, sent]);
      scrollToBottom();
    },
    onDone: () => {
      setTextAreaValue(''); setAttachments([]); setReplyTo(null);
      resetAttachmentInput(n => n + 1);
    },
  });
  useEffect(() => {
    if (!open) return;
    const job = attachmentQueue.job;
    setTextAreaValue(job?.content || ''); setAttachments(job?.files || []);
    setReplyTo(job?.replyTo || null); setEditing(null);
  // Restore only on chat/account changes or reopening, never on progress ticks.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionKey]);


  // Граница непрочитанного. Ставится при открытии и не двигается, пока чат
  // открыт: иначе разделитель убегал бы вниз по мере чтения.
  const [firstUnreadId, setFirstUnreadId] = useState(null);

  // 'connecting' | 'open' | 'error'
  const [wsStatus, setWsStatus] = useState('connecting');

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [scrollRoot, setScrollRoot] = useState(null);
  const bindScrollRoot = useCallback(node => {
    messagesContainerRef.current = node;
    setScrollRoot(node);
  }, []);
  const socketRef = useRef(null);
  const unreadDividerRef = useRef(null);

  // Наблюдатель за показом сообщений на экране
  const ioRef = useRef(null);
  const msgNodes = useRef(new Map());
  const pendingReadRef = useRef(new Set());
  const readTimerRef = useRef(null);
  const readBusyRef = useRef(false);
  const readChatRef = useRef(chatId);
  readChatRef.current = open ? chatId : null;
  useEffect(() => { pendingReadRef.current.clear(); }, [open, chatId]);
  const [visTick, setVisTick] = useState(0);

  // Участники чата — для @упоминаний. Приходят вместе с сообщениями.
  const [participants, setParticipants] = useState([]);
  // Нет доступа к чату (403): например, у сотрудника нет права «Просмотр заявок».
  const [accessDenied, setAccessDenied] = useState(false);
  const mediaArchive = useChatArchive({apiURL: API_URL, chatId, token: localStorage.getItem('client_token'), audience: 'client',
    messages, enabled: Boolean(open && chatId && !accessDenied)});

  // Ручное добавление сотрудников в чат заявки
  const [addOpen, setAddOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addList, setAddList] = useState([]);
  const [addError, setAddError] = useState('');
  const [addingId, setAddingId] = useState(null);

  // -=-=- Утилиты -=-=-
  const mergeArrays = (arr1, arr2) => {
    const merged = new Map();
    [...arr1, ...arr2].forEach((item) => merged.set(item.id, item));
    return Array.from(merged.values());
  };

  // Сообщения сотрудников и клиента живут в разных таблицах, поэтому id могут
  // совпасть. Сравниваем и вид отправителя, иначе чужое сообщение с тем же
  // номером покажется своим.
  const isMine = (m) => (
    m?.sender?.kind !== 'employee' && String(account?.id) === String(m?.sender?.id)
  );

  const needsRead = (m) => Boolean(
    m && m.is_read === false && !isMine(m) && !isTmp(m.id),
  );

  const scrollToBottom = (smooth = true) => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' });
    else messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  };

  // -=-=- Отметка прочитанного -=-=-
  const flushRead = async () => {
    if (document.visibilityState !== 'visible' || !open || !chatId) return;
    const ids = Array.from(pendingReadRef.current).slice(0, 300);
    if (!ids.length || readBusyRef.current) return;
    readBusyRef.current = true;
    const session = localStorage.getItem('client_token');
    const activeChat = chatId;
    try {
      const result = await APIMarkMessagesAsRead(activeChat, ids);
      if (!result || result.error || result.status === 'error') throw new Error('Не подтверждено прочтение');
      if (session !== localStorage.getItem('client_token') || String(readChatRef.current) !== String(activeChat)) return;
      ids.forEach(id => pendingReadRef.current.delete(id));
      const idSet = new Set(ids);
      setMessages(prev => prev.map(m => idSet.has(m.id) ? { ...m, is_read: true } : m));
      notifyRead(activeChat, ids.length);
    } catch {
      // ID остаются в очереди, следующая видимость/таймер повторит запрос.
    } finally {
      readBusyRef.current = false;
      if (pendingReadRef.current.size && readChatRef.current != null) {
        clearTimeout(readTimerRef.current);
        readTimerRef.current = setTimeout(() => readFlushRef.current(), 2000);
      }
    }
  };
  const readFlushRef = useRef(flushRead);
  readFlushRef.current = flushRead;

  const queueRead = (id) => {
    if (pendingReadRef.current.has(id)) return;
    pendingReadRef.current.add(id);
    if (readTimerRef.current) clearTimeout(readTimerRef.current);
    readTimerRef.current = setTimeout(() => readFlushRef.current(), READ_FLUSH_MS);
  };

  // Вкладку свернули — сообщения «на экране» на самом деле никто не видит.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        setVisTick(t => t + 1);
        readFlushRef.current();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    const root = scrollRoot;
    if (!open || !root || !chatId) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (document.visibilityState !== 'visible') return;
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const raw = e.target.getAttribute('data-mid');
        if (!raw) return;
        const id = Number(raw);
        if (Number.isNaN(id)) return;
        queueRead(id);
        io.unobserve(e.target);
      });
    }, { root, threshold: 0.01 });
    ioRef.current = io;
    return () => {
      io.disconnect();
      ioRef.current = null;
      if (readTimerRef.current) clearTimeout(readTimerRef.current);
    };
  }, [open, chatId, scrollRoot]);

  // Пересобираем наблюдение при изменении ленты: disconnect + observe заново,
  // чтобы видимые прямо сейчас сообщения снова дали событие.
  useEffect(() => {
    const io = ioRef.current;
    if (!io) return;
    io.disconnect();
    messages.forEach((m) => {
      if (!needsRead(m)) return;
      const node = msgNodes.current.get(m.id);
      if (node) io.observe(node);
    });
  }, [messages, visTick, account?.id, scrollRoot, open, chatId]);

  // Дополнительная проверка после монтирования/анимации окна. В некоторых
  // браузерах первый IntersectionObserver срабатывает ещё при скрытом портале.
  const scanVisibleRef = useRef(() => {});
  scanVisibleRef.current = () => {
    if (!open || !scrollRoot || document.visibilityState !== 'visible') return;
    const root = scrollRoot.getBoundingClientRect();
    if (!root.width || !root.height) return;
    messages.forEach(m => {
      if (!needsRead(m)) return;
      const node = msgNodes.current.get(m.id);
      if (!node) return;
      const rect = node.getBoundingClientRect();
      if (rect.height > 0 && rect.width > 0 && rect.bottom > root.top
          && rect.top < root.bottom && rect.right > root.left && rect.left < root.right) {
        queueRead(m.id);
      }
    });
  };
  useEffect(() => {
    if (!open || !scrollRoot) return;
    const timer = setTimeout(() => scanVisibleRef.current(), 300);
    return () => clearTimeout(timer);
  }, [messages, visTick, account?.id, scrollRoot, open, chatId]);

  const bindMsgNode = (id) => (node) => {
    if (node) msgNodes.current.set(id, node);
    else msgNodes.current.delete(id);
  };

  // -=-=- Загрузка сообщений -=-=-
  const loadMessages = async (offset = 0, limit = 30, initial = false) => {
    if (!open || !chatId || !clientToken) return;
    // A new chat/account starts its own initial request immediately, even
    // while an old chat's request is still waiting for a response.
    if (loadRequestRef.current?.sessionKey === sessionKey) return;
    const request = { sessionKey, cancelled: false };
    loadRequestRef.current = request;
    const isCurrent = () => !request.cancelled && loadRequestRef.current === request
      && activeSessionRef.current === sessionKey
      && localStorage.getItem('client_token') === clientToken;

    setLoading(true);
    try {
      // Axios подставляет токен; ответ применяем только к исходной сессии.
      // anchor=unread — окно с границы непрочитанного; markRead=false —
      // отмечаем сами, по факту показа сообщения на экране.
      const data = await APIGetChatMessagesById(
        chatId, clientToken, limit, offset,
        initial ? { anchor: 'unread', markRead: false } : { markRead: false },
      );
      if (!isCurrent()) return;
      const incoming = data?.messages?.reverse() || [];

      if (initial) {
        setMessages(incoming);
        setFirstUnreadId(data?.first_unread_id ?? null);
        setInitialLoad(false);
      } else {
        setMessages((prev) => mergeArrays(incoming, prev));
      }
      if (Array.isArray(data?.participants)) setParticipants(data.participants);
      setHasMore(!!data?.has_more);
      setAccessDenied(false);
    } catch (e) {
      if (!isCurrent()) return;
      if (e?.response?.status === 403) setAccessDenied(true);
      console.error('Ошибка загрузки сообщений:', e);
    } finally {
      if (isCurrent()) {
        loadRequestRef.current = null;
        setLoading(false);
      }
    }
  };

  // -=-=- Открытие модалки -=-=-
  useEffect(() => {
    if (loadRequestRef.current) loadRequestRef.current.cancelled = true;
    loadRequestRef.current = null;
    setLoading(false);
    setParticipants([]);
    if (open && chatId && clientToken) {
      setMessages([]);
      setHasMore(true);
      setInitialLoad(true);
      setAccessDenied(false);
      setFirstUnreadId(null);
      setShowJump(false);
      msgNodes.current.clear();
      pendingReadRef.current.clear();
      loadMessages(0, 30, true);
    }
    if (!open || !clientToken) {
      setMessages([]);
      setTextAreaValue('');
      setAttachments([]);
      setReplyTo(null);
      setEditing(null);
      setMenu(null);
      setAddOpen(false);
      setAddList([]);
      setAddError('');
    }
    return () => {
      if (loadRequestRef.current?.sessionKey === sessionKey) {
        loadRequestRef.current.cancelled = true;
        loadRequestRef.current = null;
      }
    };
  }, [open, sessionKey]);

  // Первая отрисовка: встаём на границу непрочитанного, иначе — в самый низ.
  useEffect(() => {
    if (!open || initialLoad || messages.length === 0) return undefined;
    const t = setTimeout(() => {
      const divider = unreadDividerRef.current;
      if (divider) divider.scrollIntoView({ block: 'center' });
      else scrollToBottom(false);
    }, 60);
    return () => clearTimeout(t);
  }, [initialLoad, open]);

  // -=-=- WebSocket -=-=-
  useEffect(() => {
    if (!open || !chatId || !clientToken) return undefined;
    let cancelled = false;
    const isCurrent = () => !cancelled && activeSessionRef.current === sessionKey
      && localStorage.getItem('client_token') === clientToken;

    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }

    const { close } = createWebSocketConnection(
      chatId,
      clientToken,
      (data) => {
        if (!isCurrent()) return;
        // Событие о прочтении в кабинете клиента не нужно: галочек здесь нет.
        if (data?.type === 'message_read') return;

        const _mess = data?.data?.message;
        const action = data?.data?.action;
        if (!_mess) return;

        if (!_mess.attachments) _mess.attachments = [];

        if (action === 'updated') {
          setMessages(prev => prev.map(item => item?.id === _mess.id
            ? { ..._mess, is_read: item.is_read === true || _mess.is_read === true } : item));
        } else if (action === 'deleted') {
          setMessages((prev) => prev.filter((item) => item?.id !== _mess?.id));
        } else {
          setMessages((prev) => {
            if (prev.some((m) => m.id === _mess.id)) return prev;
            // Снимаем оптимистичную копию: сообщение пришло по сокету раньше,
            // чем вернулся ответ на отправку.
            const withoutTmp = prev.filter(
              (m) => !(m?._pending && m?.content === _mess.content),
            );
            return [...withoutTmp, _mess];
          });
        }
      },
      (status) => { if (isCurrent()) setWsStatus(status); },
    );

    const connection = { close };
    socketRef.current = connection;

    return () => {
      cancelled = true;
      close();
      if (socketRef.current === connection) socketRef.current = null;
    };
  }, [open, sessionKey]);

  // -=-=- Прокрутка -=-=-
  const handleScroll = (e) => {
    scanVisibleRef.current();
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    // Контейнер во flex column-reverse: «низ» ленты — это scrollTop 0.
    setShowJump(Math.abs(scrollTop) > 240);
    if ((scrollTop * -1) >= (scrollHeight - clientHeight) - 100
        && !loading && hasMore && !initialLoad) {
      loadMessages(messages.length);
    }
  };

  const unreadInView = useMemo(
    () => messages.filter(needsRead).length,
    [messages, account?.id],
  );

  // Кнопка «вниз»: и уводит в конец ленты, и гасит остаток непрочитанного.
  const jumpDown = async () => {
    scrollToBottom();
    if (document.visibilityState !== 'visible') return;
    // Кнопка прокручивает вниз; прочитанными становятся только увиденные сообщения.
  };

  // -=-=- Отправка -=-=-
  const handleSendMessage = async (mentions = []) => {
    if (editing) { await submitEdit(); return; }
    if (!attachmentQueue.job && !attachments.length && !textAreaValue.trim()) return false;
    return attachmentQueue.submit({ content: textAreaValue, files: attachments, mentions, replyTo });
  };

  const handleSendVoice = async (blob, meta, { onProgress, signal } = {}) => {
    const sent = await APISendVoiceMessage(
      chatId, blob,
      { duration: meta?.duration, waveform: meta?.waveform, replyToId: replyTo?.id },
      onProgress, signal,
    );
    if (sent && sent.id) {
      if (!sent.attachments) sent.attachments = [];
      setMessages((prev) => (prev.some((m) => m?.id === sent.id) ? prev : [...prev, sent]));
      setReplyTo(null);
      scrollToBottom();
    }
    return sent;
  };

  // -=-=- Действия над сообщением -=-=-
  const canEditMessage = (m) => {
    if (!m || m.type_sender === 'system' || !isMine(m) || isTmp(m.id)) return false;
    // Окно правки — сутки, как на сервере: показывать пункт, который вернёт
    // отказ, смысла нет.
    return (Date.now() - new Date(m.date_create).getTime()) < 24 * 60 * 60 * 1000;
  };

  const canDeleteMessage = (m) => Boolean(
    m && m.type_sender !== 'system' && !isTmp(m.id) && isMine(m),
  );

  const handleDeleteMessage = async (m) => {
    const snapshot = messages;
    setMessages((prev) => prev.filter((x) => x?.id !== m.id));
    const res = await APIDeleteMessage(chatId, m.id);
    if (res?.status !== 'ok') {
      setMessages(snapshot);
      setActionError(res?.error || res?.detail || 'Не удалось удалить сообщение');
    }
  };

  const handleReact = async (m, emoji) => {
    const res = await APIToggleMessageReaction(chatId, m.id, emoji);
    if (res?.status === 'ok' && res?.message) {
      setMessages((prev) => prev.map((x) => (x?.id === res.message.id ? res.message : x)));
    } else if (res?.error) {
      setActionError(res.error);
    }
  };

  const startEdit = (m) => {
    setEditing(m);
    setReplyTo(null);
    setTextAreaValue(m.content || '');
  };

  const cancelEdit = () => {
    setEditing(null);
    setTextAreaValue('');
  };

  const submitEdit = async () => {
    const m = editing;
    const content = textAreaValue.trim();
    if (!m || !content) return;
    setEditing(null);
    setTextAreaValue('');
    setMessages((prev) => prev.map((x) => (
      x?.id === m.id ? { ...x, content, is_edited: true } : x
    )));
    const res = await APIEditMessage(chatId, m.id, content);
    if (res?.id) {
      setMessages((prev) => prev.map((x) => (x?.id === res.id ? res : x)));
    } else {
      setMessages((prev) => prev.map((x) => (x?.id === m.id ? m : x)));
      setEditing(m);
      setTextAreaValue(content);
      setActionError(res?.detail || res?.error || 'Не удалось изменить сообщение');
    }
  };

  const handleAttachFiles = (files) => setAttachments(files);

  // -=-=- Добавление сотрудников -=-=-
  const loadAddable = async () => {
    if (!applicationId) return;
    setAddLoading(true);
    setAddError('');
    const res = await APIClientChatAddable(applicationId);
    setAddLoading(false);
    if (res?.status === 'ok') {
      setAddList(res.accounts || []);
    } else {
      setAddError(res?.detail || 'Не удалось загрузить список сотрудников');
      setAddList([]);
    }
  };

  const toggleAddPanel = () => {
    const next = !addOpen;
    setAddOpen(next);
    if (next) loadAddable();
  };

  const handleAddPerson = async (acc) => {
    setAddingId(acc.id);
    setAddError('');
    const res = await APIClientChatAddMember(applicationId, acc.id);
    setAddingId(null);
    if (res?.status === 'ok') {
      setAddList((prev) => prev.filter((a) => a.id !== acc.id));
    } else {
      setAddError(res?.detail || 'Не удалось добавить сотрудника');
    }
  };

  // -=-=- Раскладка: секции по дням, внутри — серии сообщений -=-=-
  const isGroup = participants.length > 2;

  const sections = useMemo(() => {
    const out = [];
    let prev = null;

    const sameAuthorAs = (a, b) => {
      if (!a || !b) return false;
      if (a.type_sender === 'system' || b.type_sender === 'system') return false;
      if (String(a?.sender?.id ?? '') !== String(b?.sender?.id ?? '')) return false;
      if ((a?.sender?.kind || '') !== (b?.sender?.kind || '')) return false;
      return Math.abs(new Date(b.date_create) - new Date(a.date_create)) <= GROUP_WINDOW_MS;
    };
    const sameDay = (a, b) => a && b && dayKey(a.date_create) === dayKey(b.date_create);

    messages.forEach((m, i) => {
      const next = messages[i + 1];
      const isSystem = m?.type_sender === 'system';
      const key = dayKey(m.date_create);

      if (out.length === 0 || out[out.length - 1].key !== key) {
        out.push({ key, label: dayLabel(m.date_create), items: [] });
      }

      const withPrev = sameAuthorAs(prev, m) && sameDay(prev, m);
      const withNext = sameAuthorAs(m, next) && sameDay(m, next);

      let groupPos = 'single';
      if (withPrev && withNext) groupPos = 'middle';
      else if (withPrev) groupPos = 'last';
      else if (withNext) groupPos = 'first';

      out[out.length - 1].items.push({
        key: `msg-${m.id}`,
        message: m,
        isYou: isMine(m),
        isSystem,
        groupPos,
        showAvatar: !isSystem && (groupPos === 'last' || groupPos === 'single'),
        showName: !isSystem && isGroup && (groupPos === 'first' || groupPos === 'single'),
        unreadMark: firstUnreadId != null && m.id === firstUnreadId,
      });

      prev = m;
    });
    return out;
  }, [messages, account?.id, isGroup, firstUnreadId]);

  const renderItem = (row) => (
    <Fragment key={row.key}>
      {row.unreadMark && (
        <Box ref={unreadDividerRef} sx={(theme) => unreadDivider(theme)}>
          Непрочитанные сообщения
        </Box>
      )}

      {row.isSystem ? (
        <Box
          data-mid={isTmp(row.message?.id) ? undefined : row.message?.id}
          ref={bindMsgNode(row.message?.id)}
          sx={{ display: 'flex', justifyContent: 'center', my: 0.75 }}
        >
          <ChatBubble message={row.message} isYou={false} />
        </Box>
      ) : (
        <Stack
          data-mid={isTmp(row.message?.id) ? undefined : row.message?.id}
          ref={bindMsgNode(row.message?.id)}
          direction="row"
          spacing={1}
          onClickCapture={event => mediaArchive.capture(event, row.message)}
          sx={{
            ...(mediaArchive.selected.has(String(row.message?.id)) ? {backgroundColor: 'primary.softBg', borderRadius: '12px'} : {}),
            flexDirection: row.isYou ? 'row-reverse' : 'row',
            alignItems: 'flex-end',
            minWidth: 0,
            maxWidth: '100%',
            mt: row.groupPos === 'first' || row.groupPos === 'single' ? 0.75 : 0,
          }}
        >
          {!row.isYou && (
            <Box sx={{ width: CHAT.avatarSm, flexShrink: 0 }}>
              {row.showAvatar && (
                <AvatarWithStatus size="sm" name={row.message?.sender?.name} person={row.message?.sender} />
              )}
            </Box>
          )}
          <ChatArchiveCheckbox message={row.message} archive={mediaArchive}/>
          <ChatBubble
            message={row.message}
            isYou={row.isYou}
            onReply={setReplyTo}
            onContextMenu={(anchor, m) => setMenu({ anchor, message: m })}
            onReact={handleReact}
            showName={row.showName}
            groupPos={row.groupPos}
          />
        </Stack>
      )}
    </Fragment>
  );

  // -=-=- Render -=-=-
  //
  // Контекстное меню рисуется порталом отдельно от единственного ребёнка Modal.
  return (
    <>
    <AnimatePresence>
      {open && (
        <Modal
          open={open}
          onClose={onClose}
          sx={{ zIndex: 10012 }}
          slotProps={{
            backdrop: {
              component: motion.div,
              initial: { opacity: 0, backdropFilter: 'blur(0px)' },
              animate: { opacity: 1, backdropFilter: 'blur(8px)' },
              exit: { opacity: 0, backdropFilter: 'blur(0px)' },
              transition: { duration: 0.2, ease: 'easeInOut' },
            },
          }}
        >
          <ModalOverflow sx={{ height: 'auto', p: 0 }}>
            <ModalDialog
              size="lg"
              component={motion.div}
              initial={{ opacity: 0, y: -10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              sx={(theme) => ({
                p: 0,
                overflow: 'hidden',
                display: 'flex',
                gap: 0,
                borderRadius: '28px',
                my: '20px !important',
                flexDirection: 'column',
                position: 'relative',
                [theme.breakpoints.up('md')]: { width: 720, minHeight: '95dvh' },
                [theme.breakpoints.down('md')]: { width: '95vw', minHeight: '90dvh' },
                [theme.breakpoints.down('sm')]: {
                  width: '100vw', height: '100dvh', borderRadius: 0,
                  minHeight: '100dvh', minWidth: '100dvw',
                  m: '0px !important',
                },
              })}
            >
              {/* Шапка */}
              <Stack
                direction="row"
                sx={(theme) => ({
                  px: 2, py: 1,
                  minHeight: CHAT.headerHeight,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backdropFilter: 'blur(14px)',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(20,24,35,0.82)'
                    : 'rgba(255,255,255,0.88)',
                  position: 'relative',
                  zIndex: 4,
                })}
              >
                <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', minWidth: 0 }}>
                  <AvatarWithStatus
                    size="sm"
                    name={title || 'Чат'}
                    showStatus
                    online={wsStatus === 'open'}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="title-sm" sx={{ ...ellipsis, fontWeight: 700, fontSize: 14.5 }}>
                      {title || 'Чат'}
                    </Typography>
                    <Typography
                      level="body-xs"
                      sx={{ ...ellipsis, fontSize: 11.5, color: 'text.tertiary' }}
                    >
                      {wsStatus === 'open'
                        ? (participants.length > 0 ? `${participants.length} участника` : 'На связи')
                        : wsStatus === 'error' ? 'Соединение потеряно' : 'Подключение…'}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexShrink: 0 }}>
                  {applicationId && (
                    <IconButton
                      variant={addOpen ? 'solid' : 'plain'}
                      color={addOpen ? 'primary' : 'neutral'}
                      size="sm"
                      onClick={toggleAddPanel}
                      title="Добавить сотрудника в чат"
                      sx={(theme) => ({
                        borderRadius: '50%',
                        ...(addOpen ? {} : iconColor(theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.62)' : 'rgba(71,84,103,0.72)')),
                      })}
                    >
                      <PersonAddAltRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  )}
                  <IconButton
                    variant="plain" color="neutral" size="sm"
                    onClick={onClose}
                    sx={(theme) => ({
                      borderRadius: '50%',
                      ...iconColor(theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.62)' : 'rgba(71,84,103,0.72)'),
                    })}
                  >
                    <CloseRoundedIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Stack>
              </Stack>

              <ChatArchiveSelection archive={mediaArchive}/>

              {/* Панель добавления сотрудников в чат */}
              {addOpen && (
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider',
                           backgroundColor: 'background.body', maxHeight: 240, overflowY: 'auto' }}>
                  <Typography level="body-xs" fontWeight={800}
                              sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', color: 'primary.500', mb: 1 }}>
                    Добавить сотрудника в чат
                  </Typography>

                  {addLoading ? (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
                      <CircularProgress size="sm" />
                      <Typography level="body-sm" color="neutral">Загрузка…</Typography>
                    </Stack>
                  ) : addError ? (
                    <Typography level="body-sm" color="danger">{addError}</Typography>
                  ) : addList.length === 0 ? (
                    <Typography level="body-sm" color="neutral">Все сотрудники уже в чате</Typography>
                  ) : (
                    <Stack spacing={1}>
                      {addList.map((acc) => (
                        <Sheet key={acc.id} variant="soft" color="neutral"
                               sx={{ borderRadius: '10px', p: 1, display: 'flex', alignItems: 'center',
                                     justifyContent: 'space-between', gap: 1 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography level="body-sm" fontWeight={600} noWrap>{acc.full_name}</Typography>
                            <Typography level="body-xs" color="neutral" noWrap>{acc.phone}</Typography>
                          </Box>
                          <Button size="sm" variant="solid" color="primary"
                                  loading={addingId === acc.id}
                                  onClick={() => handleAddPerson(acc)}
                                  startDecorator={<PersonAddAltRoundedIcon sx={{ fontSize: 16 }} />}>
                            Добавить
                          </Button>
                        </Sheet>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}

              {/* Лента сообщений */}
              <Box
                ref={bindScrollRoot}
                onScroll={handleScroll}
                sx={(theme) => ({
                  flex: 1,
                  minHeight: 0,
                  px: { xs: 1.5, md: 2.5 },
                  pt: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  display: 'flex',
                  flexDirection: 'column-reverse',
                  ...canvasBackground(theme),
                  ...thinScrollbar(theme),
                })}
              >
                <Stack spacing={0.375} sx={{ justifyContent: 'flex-end', pb: 1 }}>
                  {accessDenied ? (
                    <Typography textAlign="center" level="body-sm" color="danger" sx={{ py: 4 }}>
                      Нет доступа к этому чату. Попросите администратора аккаунта выдать право «Просмотр заявок».
                    </Typography>
                  ) : (messages.length === 0 && !loading && (
                    <Stack alignItems="center" sx={{ py: 5, opacity: 0.85 }}>
                      <Box
                        sx={{
                          width: 72, height: 72, mb: 1.5, borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: 'linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(124,58,237,0.14) 100%)',
                        }}
                      >
                        <ForumRoundedIcon sx={{ fontSize: 34, color: 'primary.400' }} />
                      </Box>
                      <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        Сообщений пока нет — напишите первым
                      </Typography>
                    </Stack>
                  ))}

                  {sections.map((section) => (
                    // Секция одного дня. Чип липнет внутри ЭТОЙ секции и
                    // выталкивается чипом следующего дня — на экране всегда
                    // ровно один разделитель, без наслоения.
                    <Box key={section.key} sx={{ position: 'relative' }}>
                      <Box sx={dayChipWrap}>
                        <Box
                          component={motion.div}
                          initial={{ opacity: 0, scale: 0.94 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: MOTION.base }}
                          sx={dateChip}
                        >
                          {section.label}
                        </Box>
                      </Box>
                      <Stack spacing={0.375}>
                        {section.items.map(renderItem)}
                      </Stack>
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </Stack>

                {/* Спиннер догрузки — после ленты в разметке: контейнер
                    перевёрнут, поэтому визуально он оказывается сверху. */}
                {loading && (
                  <Stack alignItems="center" sx={{ py: 1.5 }}>
                    <CircularProgress size="sm" />
                  </Stack>
                )}
              </Box>

              {/* Ошибка действия над сообщением */}
              <AnimatePresence>
                {actionError && (
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 12 }}
                    onClick={() => setActionError('')}
                    sx={{
                      position: 'absolute', bottom: 110, left: '50%', transform: 'translateX(-50%)',
                      zIndex: 7, cursor: 'pointer',
                      px: 2, py: 1, borderRadius: '12px', maxWidth: '80%',
                      backgroundColor: 'danger.500', color: '#fff',
                      boxShadow: 'md', fontSize: 13, fontWeight: 600,
                    }}
                  >
                    {actionError}
                  </Box>
                )}
              </AnimatePresence>

              {/* Кнопка «вниз» со счётчиком непрочитанного */}
              <AnimatePresence>
                {(showJump || unreadInView > 0) && (
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, scale: 0.7, y: 8 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.7, y: 8 }}
                    transition={MOTION.spring}
                    sx={{ position: 'absolute', right: 18, bottom: 118, zIndex: 5 }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <IconButton
                        variant="solid"
                        onClick={jumpDown}
                        title="Перейти к последним сообщениям"
                        sx={(theme) => ({
                          borderRadius: '50%',
                          '--IconButton-size': '40px',
                          backgroundColor: 'background.surface',
                          boxShadow: 'md',
                          ...iconColor(theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.7)' : 'rgba(71,84,103,0.78)'),
                          '&:hover': {
                            backgroundColor: 'background.level1',
                            ...iconColor('var(--joy-palette-primary-500)'),
                          },
                        })}
                      >
                        <KeyboardArrowDownRoundedIcon />
                      </IconButton>
                      {unreadInView > 0 && (
                        <Box
                          sx={{
                            position: 'absolute', top: -6, right: -4,
                            minWidth: 18, height: 18, px: 0.5,
                            borderRadius: '9px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: 'primary.500', color: '#fff',
                            fontSize: 10.5, fontWeight: 800, lineHeight: 1,
                            pointerEvents: 'none',
                            boxShadow: '0 2px 6px rgba(79,70,229,0.45)',
                          }}
                        >
                          {unreadInView > 99 ? '99+' : unreadInView}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </AnimatePresence>

              {/* Ввод */}
              {!accessDenied && (
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', backgroundColor: 'background.body' }}>
                  <ChatQueueStatus job={attachmentQueue.job}
                    onRetry={() => attachmentQueue.submit()} onDiscard={attachmentQueue.discard} />
                  <MessageInput
                    key={`${sessionKey}:${attachmentReset}`}
                    attachments={attachments}
                    disabled={Boolean(attachmentQueue.job)}
                    textAreaValue={textAreaValue}
                    setTextAreaValue={setTextAreaValue}
                    onSubmit={handleSendMessage}
                    onSendVoice={handleSendVoice}
                    onAttach={handleAttachFiles}
                    participants={participants}
                    replyTo={replyTo}
                    onCancelReply={() => setReplyTo(null)}
                    editing={editing}
                    onCancelEdit={cancelEdit}
                  />
                </Box>
              )}
            </ModalDialog>
          </ModalOverflow>
        </Modal>
      )}
    </AnimatePresence>

    {/* Контекстное меню сообщения. Без строки «кто посмотрел»:
        клиенту прочтения сотрудников не показываем. */}
    {open && menu && (
      <MessageContextMenu
        anchor={menu.anchor}
        message={menu.message}
        canEdit={canEditMessage(menu.message)}
        canDelete={canDeleteMessage(menu.message)}
        onReply={setReplyTo}
        onEdit={startEdit}
        onDelete={handleDeleteMessage}
        onReact={(emoji) => handleReact(menu.message, emoji)}
        onSelectFiles={hasArchiveMedia(menu.message) ? mediaArchive.toggle : undefined}
        onClose={() => setMenu(null)}
      />
    )}

    </>
  );
}
